import { useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'

import { useLocale } from '@/app/context/LocaleContext'
import {
  LOCALE_LABELS,
  RELEASED_LOCALES,
  ReleasedLocale,
} from '@/app/lib/locale'

function GlobeIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3c2.5 2.7 2.5 15.3 0 18M12 3c-2.5 2.7-2.5 15.3 0 18" />
    </svg>
  )
}

function ChevronIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <path d="M6 9l6 6 6-6" />
    </svg>
  )
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      aria-hidden="true"
    >
      <path d="M5 13l4 4L19 7" />
    </svg>
  )
}

type LanguageSelectorProps = {
  className?: string
}

export function LanguageSelector({ className = '' }: LanguageSelectorProps) {
  const intl = useIntl()
  const { locale, changeLocale } = useLocale()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return

    const handlePointer = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }

    document.addEventListener('mousedown', handlePointer)
    document.addEventListener('keydown', handleKey)
    return () => {
      document.removeEventListener('mousedown', handlePointer)
      document.removeEventListener('keydown', handleKey)
    }
  }, [open])

  const ariaLabel = intl.formatMessage({
    id: 'LanguageSelector.ariaLabel',
    defaultMessage: 'Language',
  })

  const currentLabel = LOCALE_LABELS[locale as ReleasedLocale] ?? LOCALE_LABELS.en

  const handleSelect = (next: ReleasedLocale) => {
    changeLocale(next)
    setOpen(false)
  }

  return (
    <div ref={ref} className={`relative ${className}`}>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={() => setOpen((value) => !value)}
        className="inline-flex h-[34px] items-center gap-2 rounded-full border border-lines bg-white px-3 text-sm font-medium text-mydra-black transition-colors hover:border-mydra-lilas hover:bg-mydra-light-purple focus:outline-none focus-visible:ring-2 focus-visible:ring-mydra-purple"
      >
        <GlobeIcon className="h-4 w-4 text-mydra-purple" />
        <span className="text-gray-light">{currentLabel}</span>
        <ChevronIcon
          className={`h-3.5 w-3.5 text-mydra-medium-gray transition-transform ${
            open ? 'rotate-180' : ''
          }`}
        />
      </button>

      {open && (
        <ul
          role="listbox"
          aria-label={ariaLabel}
          className="absolute right-0 top-[42px] z-20 w-52 rounded-2xl border border-lines bg-white p-1.5 shadow-auto-complete"
        >
          {RELEASED_LOCALES.map((code) => {
            const selected = code === locale
            return (
              <li key={code} role="option" aria-selected={selected}>
                <button
                  type="button"
                  onClick={() => handleSelect(code)}
                  className={`flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-sm transition-colors ${
                    selected
                      ? 'bg-mydra-light-purple font-semibold text-mydra-dark-purple'
                      : 'font-medium text-mydra-black hover:bg-mydra-gray'
                  }`}
                >
                  <span>{LOCALE_LABELS[code]}</span>
                  {selected && (
                    <CheckIcon className="ml-auto h-4 w-4 text-mydra-purple" />
                  )}
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
